import { useReducedMotion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { isLowPower, routeKey } from "../utils/perf";

// Per-page "universe" accents — every route drifts through its own cosmos:
// warm comets on Home, F1 speed-trails in the Repair Lab pit lane, falling
// stars over the phone galaxy, an aurora shelf in the showroom, night
// grand-prix light trails on Visit and a cyan warp gate behind the quote.
// Pure CSS, transform/opacity only, capped element counts. Low-power and
// reduced-motion devices get the static nebula aura with no moving parts.
const UNIVERSES = {
  "/": {
    aura: { c: "rgba(233,180,76,0.15)", x: "68%", y: "-20%", s: 620 },
    streaks: [
      { top: "16%", dur: 26, delay: 2, len: 34, c: "rgba(233,180,76,0.5)", ang: -14 },
      { top: "44%", dur: 34, delay: 11, len: 26, c: "rgba(240,146,62,0.38)", ang: -14 },
      { top: "70%", dur: 30, delay: 20, len: 30, c: "rgba(79,179,191,0.32)", ang: -14 },
    ],
  },
  "/repair-lab": {
    // Pit lane: fast low light trails — taillights one way, headlights back.
    aura: { c: "rgba(46,107,230,0.13)", x: "-12%", y: "58%", s: 560 },
    streaks: [
      { top: "62%", dur: 9, delay: 0, len: 42, c: "rgba(226,58,46,0.45)", ang: -4 },
      { top: "70%", dur: 7.5, delay: 2.6, len: 38, c: "rgba(237,241,247,0.4)", ang: -4, rev: true },
      { top: "56%", dur: 11, delay: 5.4, len: 46, c: "rgba(233,180,76,0.38)", ang: -4 },
      { top: "80%", dur: 8.5, delay: 7.6, len: 36, c: "rgba(107,155,255,0.38)", ang: -4, rev: true },
    ],
  },
  "/phones": {
    aura: { c: "rgba(124,131,219,0.15)", x: "58%", y: "28%", s: 640 },
    streaks: [
      { top: "12%", dur: 24, delay: 3, len: 30, c: "rgba(169,196,245,0.48)", ang: -32 },
      { top: "38%", dur: 31, delay: 14, len: 24, c: "rgba(124,131,219,0.42)", ang: -32 },
      { top: "64%", dur: 27, delay: 21, len: 28, c: "rgba(233,180,76,0.32)", ang: -32 },
    ],
  },
  "/showroom": {
    aura: { c: "rgba(79,179,191,0.13)", x: "18%", y: "10%", s: 600 },
    streaks: [
      { top: "24%", dur: 36, delay: 1, len: 40, c: "rgba(79,179,191,0.38)", ang: -8 },
      { top: "56%", dur: 30, delay: 12, len: 32, c: "rgba(124,131,219,0.38)", ang: -8 },
    ],
  },
  "/visit": {
    // Night grand prix: long-exposure traffic trails near the horizon.
    aura: { c: "rgba(199,91,18,0.13)", x: "76%", y: "68%", s: 580 },
    streaks: [
      { top: "82%", dur: 10, delay: 0, len: 44, c: "rgba(226,58,46,0.42)", ang: 0 },
      { top: "86%", dur: 12, delay: 3.4, len: 40, c: "rgba(233,180,76,0.38)", ang: 0 },
      { top: "78%", dur: 9, delay: 6.1, len: 42, c: "rgba(237,241,247,0.36)", ang: 0, rev: true },
    ],
  },
  "/quote": {
    aura: { c: "rgba(79,179,191,0.14)", x: "50%", y: "-16%", s: 620 },
    streaks: [
      { top: "22%", dur: 18, delay: 0.8, len: 36, c: "rgba(79,179,191,0.48)", ang: -20 },
      { top: "50%", dur: 22, delay: 9, len: 30, c: "rgba(107,155,255,0.42)", ang: -20 },
      { top: "74%", dur: 20, delay: 15, len: 34, c: "rgba(233,180,76,0.32)", ang: -20 },
    ],
  },
};

export const UniverseLayer = () => {
  const reduce = useReducedMotion();
  const low = isLowPower();
  const { pathname } = useLocation();
  const uni = UNIVERSES[routeKey(pathname)] || UNIVERSES["/"];
  const still = reduce || low;

  return (
    <div
      data-testid="universe-layer"
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
    >
      <div
        className="uni-aura"
        style={{
          left: uni.aura.x,
          top: uni.aura.y,
          width: uni.aura.s,
          height: uni.aura.s,
          background: `radial-gradient(circle at 40% 40%, ${uni.aura.c}, transparent 65%)`,
          animation: still ? "none" : undefined,
        }}
      />
      {!still &&
        uni.streaks.map((s, i) => (
          <span
            key={i}
            className="uni-streak"
            style={{
              top: s.top,
              width: `${s.len}vw`,
              background: `linear-gradient(90deg, transparent, ${s.c}, transparent)`,
              "--ang": `${s.ang}deg`,
              animationName: s.rev ? "uni-streak-rtl" : "uni-streak-ltr",
              animationDuration: `${s.dur}s`,
              animationDelay: `${s.delay}s`,
            }}
          />
        ))}
    </div>
  );
};
